/**
 * Lightweight persistence helpers for recent trip snapshots.
 * These snapshots power the landing page's expandable list of generated trips.
 */

export const RECENT_TRIPS_STORAGE_KEY = 'tripgen.recentTrips';
export const RECENT_TRIPS_EVENT_NAME = 'tripgen.recentTripsUpdated';
export const MAX_RECENT_TRIPS = 6;

export interface RecentTripSnapshot {
  id: string;
  destination: string;
  startDate?: string;
  endDate?: string;
  totalDays?: number;
  summary?: string;
  searchId?: string | null;
  lastPrompt?: string;
  createdAt: string;
}

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const sanitizeTrips = (raw: string | null): RecentTripSnapshot[] => {
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item): RecentTripSnapshot | null => {
        if (!item || typeof item !== 'object') {
          return null;
        }

        const destination = typeof item.destination === 'string' ? item.destination.trim() : '';
        if (!destination) {
          return null;
        }

        const id = typeof item.id === 'string' ? item.id : `${destination}-${item.startDate || ''}-${item.endDate || ''}`;

        return {
          id,
          destination,
          startDate: typeof item.startDate === 'string' ? item.startDate : undefined,
          endDate: typeof item.endDate === 'string' ? item.endDate : undefined,
          totalDays: typeof item.totalDays === 'number' ? item.totalDays : undefined,
          summary: typeof item.summary === 'string' ? item.summary : undefined,
          searchId: typeof item.searchId === 'string' ? item.searchId : item.searchId ?? null,
          lastPrompt: typeof item.lastPrompt === 'string' ? item.lastPrompt : undefined,
          createdAt: typeof item.createdAt === 'string' ? item.createdAt : new Date().toISOString(),
        };
      })
      .filter((item): item is RecentTripSnapshot => Boolean(item));
  } catch {
    return [];
  }
};

const dispatchUpdate = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(RECENT_TRIPS_EVENT_NAME));
};

export const readRecentTrips = (): RecentTripSnapshot[] => {
  const storage = getStorage();
  if (!storage) {
    return [];
  }

  const trips = sanitizeTrips(storage.getItem(RECENT_TRIPS_STORAGE_KEY));
  return trips.sort((a, b) => {
    const aTime = new Date(a.createdAt).getTime();
    const bTime = new Date(b.createdAt).getTime();
    return bTime - aTime;
  });
};

export const saveRecentTripSnapshot = (
  snapshot: Omit<RecentTripSnapshot, 'createdAt'> & Partial<Pick<RecentTripSnapshot, 'createdAt'>>,
): RecentTripSnapshot[] => {
  const storage = getStorage();
  if (!storage) {
    return [];
  }

  const normalized: RecentTripSnapshot = {
    ...snapshot,
    id: snapshot.id || `${snapshot.destination}-${snapshot.startDate || 'flex'}-${snapshot.endDate || 'tbd'}`,
    destination: snapshot.destination.trim(),
    startDate: snapshot.startDate,
    endDate: snapshot.endDate,
    totalDays: snapshot.totalDays,
    summary: snapshot.summary,
    searchId: snapshot.searchId ?? null,
    lastPrompt: snapshot.lastPrompt,
    createdAt: snapshot.createdAt || new Date().toISOString(),
  };

  if (!normalized.destination) {
    return readRecentTrips();
  }

  const existing = readRecentTrips();

  const deduped = existing.filter(
    (trip) =>
      trip.id !== normalized.id &&
      (normalized.searchId ? trip.searchId !== normalized.searchId : true),
  );

  const next = [normalized, ...deduped].slice(0, MAX_RECENT_TRIPS);
  storage.setItem(RECENT_TRIPS_STORAGE_KEY, JSON.stringify(next));
  dispatchUpdate();
  return next;
};

export const clearRecentTrips = (): void => {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  storage.removeItem(RECENT_TRIPS_STORAGE_KEY);
  dispatchUpdate();
};
